import React from 'react';
import Icon from '../app_components/Icon';
import StandardButton from '../app_components/StandardButton';
import ProfilePictureSection from '../setting_components/sections/ProfilePictureSection';
import OnboardingToast, { bindOnboardingToastHandlers } from './OnboardingToast';
import { isMissingAvatar } from '../connect_sections/connectMemberHelpers';
import { semantic } from '@lexody/design-tokens';

class SignUpUploadPicture extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      user: null,
      loaded: false,
      toastMessage: null,
      avatarConfirmed: false,
    };

    bindOnboardingToastHandlers(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.onAvatarUploaded = this.onAvatarUploaded.bind(this);
    this.onAvatarConfirmed = this.onAvatarConfirmed.bind(this);
    this.onAvatarBroken = this.onAvatarBroken.bind(this);
    this.onUploadError = this.onUploadError.bind(this);
  }

  componentDidMount() {
    $.ajax({
      url: '/get_current_user',
      dataType: 'json',
      type: 'GET',
      success: (data) => {
        const user = JSON.parse(data.data.current_user);
        this.setState({ user, loaded: true });
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onAvatarUploaded(avatarUrl) {
    this.setState((prev) => ({
      user: { ...prev.user, avatar_url: avatarUrl },
    }));
    this.clearOnboardingToast();
  }

  onAvatarConfirmed() {
    this.setState({ avatarConfirmed: true });
    this.clearOnboardingToast();
  }

  onAvatarBroken() {
    this.setState({ avatarConfirmed: false });
    this.showOnboardingToast('That photo could not be loaded. Please upload a new one.');
  }

  onUploadError(message) {
    this.setState({ avatarConfirmed: false });
    this.showOnboardingToast(message || 'Upload failed. Please try again.');
  }

  hasPhoto() {
    return this.state.avatarConfirmed;
  }

  handleContinue() {
    if (!this.hasPhoto()) {
      this.showOnboardingToast('Please upload a picture of yourself before continuing.');
      return;
    }

    window.location.assign('/complete_profile');
  }

  componentWillUnmount() {
    this.clearOnboardingToast();
  }

  renderExamples() {
    return (
      <div className="onboarding-upload__examples onboarding-page__card">
        <div className="onboarding-upload__examplesGrid">
          <div className="onboarding-upload__exampleGroup">
            <p className="onboarding-upload__exampleLabel onboarding-upload__exampleLabel--good">Good pics</p>
            <div className="picture_examples">
              <div className="good_pics">
                <div className="example" id="good1" />
                <div className="example" id="good2" />
                <div className="example" id="good3" />
              </div>
            </div>
          </div>
          <div className="onboarding-upload__exampleGroup">
            <p className="onboarding-upload__exampleLabel onboarding-upload__exampleLabel--bad">Bad pics</p>
            <div className="picture_examples">
              <div className="bad_pics">
                <div className="example" id="bad1" />
                <div className="example" id="bad2" />
                <div className="example" id="bad3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.loaded || !this.state.user) {
      return (
        <div className="onboarding-page-wrap">
          <div className="onboarding-page">
            <p className="onboarding-page__subtitle" style={{ paddingLeft: 0, textAlign: 'center' }}>Loading…</p>
          </div>
        </div>
      );
    }

    return (
      <div className="onboarding-page-wrap">
        <div className="onboarding-page">
          <header className="onboarding-page__header">
            <div className="onboarding-page__titleRow">
              <span className="onboarding-page__iconCircle" aria-hidden="true">
                <Icon name="image" size={22} color={semantic.primary.text.label} solid />
              </span>
              <h1 className="onboarding-page__title">Add your photo</h1>
            </div>
            <p className="onboarding-page__subtitle">
              Upload a <strong>good</strong> picture so partners know who they&apos;re meeting
            </p>
          </header>

          {this.renderExamples()}

          <div className="lex-dash-card onboarding-page__section">
            <SignUpAvatarUpload
              user={this.state.user}
              onAvatarUploaded={this.onAvatarUploaded}
              onAvatarConfirmed={this.onAvatarConfirmed}
              onAvatarBroken={this.onAvatarBroken}
              onUploadError={this.onUploadError}
            />
          </div>

          <div className="onboarding-page__footer">
            <OnboardingToast message={this.state.toastMessage} />
            <StandardButton
              text="Continue"
              variant="primary"
              fullWidth
              size="large"
              onClick={this.handleContinue}
            />
          </div>
        </div>
      </div>
    );
  }
}

class SignUpAvatarUpload extends ProfilePictureSection {
  uploadImage(image) {
    this.setState({ loading: true, uploadError: null, avatarImageFailed: false });
    const formData = new FormData();
    const ext = (image.name && image.name.split('.').pop()) || 'png';
    const filename = `${this.props.user.first_name || 'profile'}.${ext}`;
    formData.append('avatar', image, filename);

    $.ajax({
      url: '/update_user_avatar',
      dataType: 'JSON',
      type: 'POST',
      processData: false,
      contentType: false,
      data: formData,
      success: (data) => {
        const avatarUrl = data.data.avatar_url;
        this.setState({ avatar_url: avatarUrl, loading: false, avatarImageFailed: false, uploadError: null });
        if (this.props.onAvatarUploaded) {
          this.props.onAvatarUploaded(avatarUrl);
        }
      },
      error: (xhr, status, err) => {
        console.log(err);
        const message = this.parseUploadError(xhr);
        this.setState({ loading: false, uploadError: message });
        if (this.props.onUploadError) {
          this.props.onUploadError(message);
        }
      },
    });
  }

  render() {
    const hasPhoto = !isMissingAvatar(this.state.avatar_url) && !this.state.avatarImageFailed;
    return (
      <div className="lex-avatar-upload">
        <div
          className="lex-avatar-upload__frame"
          onClick={() => this.triggerInputFile()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.triggerInputFile();
            }
          }}
        >
          {this.renderImage()}
        </div>
        <StandardButton
          text={hasPhoto ? 'Change photo' : 'Upload photo'}
          variant="secondary"
          onClick={() => this.triggerInputFile()}
        />
        {this.state.uploadError && (
          <p className="lex-avatar-upload__error" role="alert">{this.state.uploadError}</p>
        )}
        <input ref={this.fileInput} type="file" hidden onChange={this.onFileChange} accept="image/*" />
      </div>
    );
  }
}

export default SignUpUploadPicture;
